import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { NetworkSubmissionContext } from "@app/compliance-connect/network-submission/NetworkSubmissionContext";
import { NetworkSubmissionWizardAction } from "@app/compliance-connect/network-submission/networkSubmissionWizardActions";
import NetworkSubmissionService from "@app/compliance-connect/network-submission/networkSubmissionService";
import { useDebouncedValue } from "@app/shared/hooks/useDebouncedValue";
import { UserContext } from "@app/user/UserContext";
import UserService from "@app/user/userService";

interface SubmissionSummaryRow {
  type: string;
  count: number | string;
}

const networkSubmissionService = new NetworkSubmissionService();
const userService = new UserService();

const CONFIRMATION_TEXT =
  "I confirm that all the information I am about to submit is correct. I acknowledge that submitting inaccurate information may result in penalties or delays in the review process.";

export const NetworkSubmissionStepReview: FC = () => {
  const wizardContext = useContext(NetworkSubmissionContext);
  const user = useContext(UserContext);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const submittedByName = user ? `${user.firstName} ${user.lastName}` : "";
  const submissionTimeRef = useRef<string>(
    new Date().toLocaleString("en-US", {
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  );
  const [selectedSharedUsers, setSelectedSharedUsers] = useState<DropdownItem<number>[]>([]);

  const { data: sharedUsersData, isLoading: sharedUsersLoading } = useQuery({
    enabled: !!user?.clientId,
    queryFn: () => userService.getMinimalUsersByClient(user!.clientId),
    queryKey: ["ns-shared-users", user?.clientId],
  });

  const sharedUserItems = useMemo<DropdownItem<number>[]>(() =>
    (sharedUsersData ?? []).map(u => ({ key: u.userId, name: u.fullName })),
    [sharedUsersData]
  );

  const project = wizardContext.selectedProject;
  const exceptionsCount = project ? project.exceptions + wizardContext.pendingExceptions.size : "—";

  const summaryRows: SubmissionSummaryRow[] = [
    { count: 1, type: "Projects" },
    { count: project?.counties ?? "—", type: "Counties" },
    { count: project?.specialties ?? "—", type: "Specialties" },
    { count: project?.gaps ?? "—", type: "Gaps" },
    { count: exceptionsCount, type: "Exceptions" },
  ];

  const checkSubmissionName = async () => {
    if (wizardContext.submissionName.trim() === "") {
      setIsDuplicate(false);
      return;
    }

    const exists = await networkSubmissionService.checkNameExists(wizardContext.submissionName);
    setIsDuplicate(exists);
  };

  const debouncedSubmissionName = useDebouncedValue(wizardContext.submissionName, 300);

  useEffect(() => {
    void checkSubmissionName();
  }, [debouncedSubmissionName]);

  return (
    <div className="row mt-2">

      <div className="col-7">
        <div className="mb-4">
          <h6 className="demibold mb-3">Network Submission Name<span className="text-danger"> *</span></h6>
          <QESTextInput
            label="Network Submission Name"
            hideLabel={true}
            testId="ns-review__submission-name"
            isValid={wizardContext.submissionName.trim() !== "" && !isDuplicate}
            unrequiredValidation={true}
            wasFocusedExternally={isDuplicate}
            errorText={wizardContext.submissionName.trim() === ""
              ? "Network Submission Name is required."
              : "A submission with this name already exists."}
            value={wizardContext.submissionName}
            onChange={(e) => wizardContext.dispatch({ payload: e.target.value, type: NetworkSubmissionWizardAction.SetSubmissionName })}
          />
        </div>

        <div className="mb-4">
          <h6 className="demibold mb-3">Submission Summary</h6>
          <div className="ns-review__summary">
            <div className="ns-review__summary-header">
              <span>Type</span>
              <span>Count</span>
            </div>
            <dl className="mb-0">
              {summaryRows.map((row) => (
                <div key={row.type} className="ns-review__summary-row">
                  <dt className="ns-review__summary-label">{row.type}</dt>
                  <dd>{row.count}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="mb-4">
          <h6 className="demibold mb-3">Network Submission Description<span className="text-danger"> *</span></h6>
          <QESTextArea
            label="Network Submission Description"
            containerClassName="ns-review__hidden-label"
            testId="ns-review__submission-description"
            isValid={wizardContext.submissionDescription.trim() !== ""}
            errorText="Network Submission Description is required."
            value={wizardContext.submissionDescription}
            rows={4}
            onChange={(e) => wizardContext.dispatch({ payload: e.target.value, type: NetworkSubmissionWizardAction.SetSubmissionDescription })}
          />
        </div>
      </div>

      <div className="col-5">
        <div className="mb-4">
          <h6 className="demibold mb-3">Comments</h6>
          <QESTextArea
            label="Comments"
            containerClassName="ns-review__hidden-label"
            testId="ns-review__comments"
            placeholder="Write a comment to a regulator"
            value={wizardContext.comments}
            rows={6}
            onChange={(e) => wizardContext.dispatch({ payload: e.target.value, type: NetworkSubmissionWizardAction.SetComments })}
          />
        </div>

        <div className="mb-4">
          <h6 className="demibold mb-3">Update Configuration</h6>
          <div className="ns-review__update-config-box">
            <div className="small demibold muted">Shared Users</div>
            <div className="row align-items-center">
              <div className="col-5">
                <MultiselectDropdown<number>
                  items={sharedUserItems}
                  label="Shared Users"
                  hideLabel={true}
                  hideSelectAll={true}
                  isLoading={sharedUsersLoading}
                  itemAlias="User(s) Selected"
                  selectedItems={selectedSharedUsers}
                  onSelectionChanged={(items) => {
                    setSelectedSharedUsers(items);
                    wizardContext.dispatch({
                      payload: items.map(i => i.key),
                      type: NetworkSubmissionWizardAction.SetSharedUserIds,
                    });
                  }}
                  testId="ns-review__shared-users"
                  containerClassName="d-block"
                />
              </div>
              <div className="col-7">
                <QESCheckbox
                  label="Email the selected users when there is a response or status update."
                  testId="ns-review__email-shared-users"
                  checked={wizardContext.emailSharedUsers}
                  onChange={(e) => wizardContext.dispatch({ payload: e.target.checked, type: NetworkSubmissionWizardAction.SetEmailSharedUsers })}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h6 className="demibold mb-3">Confirmation</h6>
          <div className="ns-review__confirmation-box">
            <div className="row mb-2">
              <div className="col-6 text-muted small demibold">Submitted by</div>
              <div className="col-6 text-muted small demibold">Submission Time</div>
            </div>
            <div className="row mb-3">
              <div className="col-6 small" data-testid="ns-review__submitted-by">{submittedByName}</div>
              <div className="col-6 small" data-testid="ns-review__submission-time">{submissionTimeRef.current}</div>
            </div>
            <QESCheckbox
              label={CONFIRMATION_TEXT}
              testId="ns-review__confirm-checkbox"
              containerClassName="ns-review__confirm-checkbox"
              required={true}
              checked={wizardContext.isConfirmed}
              onChange={(e) => wizardContext.dispatch({ payload: e.target.checked, type: NetworkSubmissionWizardAction.SetIsConfirmed })}
            />
          </div>
        </div>

      </div>
    </div>
  );
};
